import { cadreApercu, isStudioEmbed } from './actions';

export type Landmark = {
  selector: string;
  text: string;
  tag: string;
  href?: string;
};

function escapeIdent(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return value.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}

function cssPath(el: Element): string {
  if (el.id) return `#${escapeIdent(el.id)}`;
  const parts: string[] = [];
  let node: Element | null = el;
  while (node && parts.length < 5) {
    const parent: Element | null = node.parentElement;
    if (!parent) break;
    const tag = node.tagName.toLowerCase();
    const siblings = Array.from(parent.children).filter((c) => c.tagName === node!.tagName);
    const idx = siblings.indexOf(node) + 1;
    parts.unshift(siblings.length > 1 ? `${tag}:nth-of-type(${idx})` : tag);
    if (parent.id) {
      parts.unshift(`#${escapeIdent(parent.id)}`);
      break;
    }
    node = parent;
  }
  return parts.join(' > ');
}

function isStudioChrome(el: HTMLElement): boolean {
  if (el.closest('#genious-root')) return true;
  if (el.closest('.topbar, header.topbar, .nav-links')) return true;
  // Liens internes Studio (préview / dashboard)
  if (el instanceof HTMLAnchorElement) {
    try {
      const u = new URL(el.href, window.location.href);
      if (u.origin === window.location.origin) {
        if (/^\/(dashboard|sites|onboarding|privacy|login|settings)/.test(u.pathname)) {
          return true;
        }
      }
    } catch {
      /* ignore */
    }
  }
  return false;
}

function collectFromDocument(doc: Document, limit: number): Landmark[] {
  const nodes = Array.from(
    doc.querySelectorAll(
      'h1, h2, h3, main a[href], [role="main"] a[href], nav a[href], header a[href], footer a[href], button, [data-genious-target]'
    )
  );
  const out: Landmark[] = [];
  const seen = new Set<string>();

  for (const el of nodes) {
    if (!(el instanceof HTMLElement)) continue;
    if (isStudioEmbed() && isStudioChrome(el)) continue;
    if (el.closest('#genious-root')) continue;
    const text = (el.innerText || el.getAttribute('aria-label') || '').replace(/\s+/g, ' ').trim();
    if (text.length < 2 || text.length > 120) continue;
    const rect = el.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) continue;
    const selector = cssPath(el);
    if (!selector || seen.has(selector)) continue;
    seen.add(selector);
    const href =
      el instanceof HTMLAnchorElement && el.href
        ? el.href
        : el.getAttribute('href') || undefined;
    out.push({
      selector,
      text,
      tag: el.tagName,
      ...(href ? { href } : {}),
    });
    if (out.length >= limit) break;
  }
  return out;
}

export function collectLandmarks(limit = 50): Landmark[] {
  // En Studio : les repères du site affiché dans le cadre, s'il est lisible
  // (même origine). Sinon aucun : ceux de la page du Studio ne décrivent pas le
  // site, et l'API y cherchait de quoi guider le visiteur.
  if (isStudioEmbed()) {
    try {
      const doc = cadreApercu()?.contentDocument;
      if (doc?.body) return collectFromDocument(doc, limit);
    } catch {
      /* cross-origin */
    }
    return [];
  }

  return collectFromDocument(document, limit);
}

/**
 * L'adresse et le titre de la page que le visiteur regarde.
 *
 * Dans l'aperçu du Studio, c'est le site affiché dans le cadre, pas le Studio :
 * envoyer l'adresse du Studio privait l'API du site dont il est question, donc
 * de toute navigation (audit modelabs). `src` suffit : une navigation dans
 * l'aperçu passe par lui (`navigateTo`). Un clic du visiteur à l'intérieur du
 * cadre reste invisible — la politique d'origine l'interdit.
 */
export function pageRegardee(): { url: string; title?: string } {
  if (isStudioEmbed()) {
    const src = cadreApercu()?.src;
    // Sans titre : illisible dans le cadre, et l'API retombe sur le nom du site.
    if (src && /^https?:/.test(src)) return { url: src };
  }
  return { url: location.href, title: document.title };
}
